import { useMutation, keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/constants';
import { FaceIdProfileService } from '@/services';

import {
  RoleAssignmentCreationService,
  RoleAssignmentUpdateService,
  RoleAssignmentService,
} from '@/features/role-assignments/services';
import {
  IRoleAssignmentUpdate,
  IRoleAssignmentSearchingPayload,
} from '@/features/role-assignments/interfaces';

export const useRoleAssignmentQuery = (profileId?: string) => {
  const faceIdProfileQuery = useQuery({
    queryKey: QUERY_KEYS.faceIdProfile(profileId || ''),
    queryFn: async () => {
      const response = await FaceIdProfileService.GetFaceIdProfile(profileId || '');
      return response.data;
    },
    enabled: !!profileId,
  });
  const rolesQuery = useQuery({
    queryKey: QUERY_KEYS.roles,
    queryFn: async () => {
      const response = await RoleAssignmentService.GetRoles();
      return response.data;
    },
  });
  const useRolesSearching = (payload: IRoleAssignmentSearchingPayload) => {
    return useQuery({
      queryKey: QUERY_KEYS.roleAssignmentsSearching(payload),
      queryFn: () => RoleAssignmentService.RolesSearching(payload),
      placeholderData: keepPreviousData,
    });
  };

  return { faceIdProfileQuery, rolesQuery, useRolesSearching };
};

export const useRoleAssignmentMutation = (id?: string) => {
  const queryClient = useQueryClient();
  const creationMutation = useMutation({
    mutationFn: async (roleAssignmentData: IRoleAssignmentUpdate) => {
      const response = await RoleAssignmentCreationService.CreateRoleAssignment(roleAssignmentData);
      return response.data;
    },
  });
  const updateMutation = useMutation({
    mutationFn: async (roleAssignmentData: IRoleAssignmentUpdate) => {
      const response = await RoleAssignmentUpdateService.UpdateRoleAssignment(roleAssignmentData);
      return response.data;
    },
    onSuccess: () => {
      if (id) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roleAssignment(id) });
      }
    },
  });
  return {
    creationMutation,
    updateMutation,
  };
};
